import { useState } from 'react';
import {
  AMAZONS_SIZE,
  amazonsTerritoryOwners,
  amazonsRemainingSummaries,
  type AmazonsMove,
  type AmazonsSelection,
  type AmazonsState,
  moveNotation,
  previewAmazonsBoard,
  regionBoundsLabel,
  selectAmazonsSquare,
  type Side,
  sideLabel,
  squareName,
  territoryProofBoard,
  territoryReason,
} from '../../games/amazons';
import { BoardCoords } from './BoardCoords';
import './AmazonsBoard.css';
import './OwnFrontRim.css';

interface Props {
  state: AmazonsState;
  legal?: AmazonsMove[];
  yourSide?: Side | null;
  myTurnAllowed?: boolean;
  onMove?: (move: AmazonsMove) => void;
  mini?: boolean;
  lastMove?: AmazonsMove;
  staticView?: boolean;
  suppressTurnStatus?: boolean;
}

export function AmazonsBoard({ state, legal, yourSide, myTurnAllowed, onMove, mini, lastMove, staticView, suppressTurnStatus = false }: Props) {
  const isMyTurn = myTurnAllowed ?? (yourSide != null && yourSide === state.to_move);
  const canInteract = !mini && !staticView && isMyTurn && !state.outcome && !!onMove;
  // snapshot の再生成では維持し、別の着手・再開・手番変更では選択を必ず無効にする。
  const positionKey = `${state.ply}:${state.to_move}:${state.board.join(',')}`;
  const [pending, setPending] = useState<{ position: string; selection: AmazonsSelection } | null>(null);
  const selection = canInteract && pending?.position === positionKey ? pending.selection : null;
  const [feedback, setFeedback] = useState<{ position: string; text: string } | null>(null);
  const [proof, setProof] = useState<{ position: string; region: number; step: number } | null>(null);
  const territory = state.outcome?.territory;
  const activeProof = !mini && territory && proof?.position === positionKey ? proof : null;
  const activeRegion = activeProof && territory ? territory.regions[activeProof.region] : null;
  const board = activeRegion && activeProof
    ? territoryProofBoard(state.board, activeRegion, activeProof.step)
    : previewAmazonsBoard(state.board, selection);
  const owners = amazonsTerritoryOwners(board);
  const remaining = amazonsRemainingSummaries(state);
  const sources = new Set((legal ?? []).map((move) => move.from));
  const candidates = selection ? (legal ?? []).filter((move) => move.from === selection.from) : [];
  const targets = new Set(candidates.map((move) => move.to));
  const arrows = new Set(candidates.filter((move) => move.to === selection?.to).map((move) => move.arrow));
  const flip = yourSide === 'Second';
  const rows = Array.from({ length: AMAZONS_SIZE }, (_, index) => flip ? index : AMAZONS_SIZE - 1 - index);
  const cols = Array.from({ length: AMAZONS_SIZE }, (_, index) => flip ? AMAZONS_SIZE - 1 - index : index);
  const last = lastMove ?? state.last_move;
  const proofNumbers = new Map(activeRegion?.line.map((move, index) => [move.arrow, index + 1]));
  const proofCells = new Set(activeRegion ? [...activeRegion.cells, ...activeRegion.amazons] : []);
  const showWait = !mini && !staticView && !isMyTurn && !state.outcome;
  const stage = !selection ? 'select' : selection.to === null ? 'move' : 'arrow';
  const instruction = stage === 'select'
    ? '① 自分のアマゾンを選択'
    : stage === 'move'
      ? `② ${squareName(selection!.from)} の移動先を選択`
      : `③ ${squareName(selection!.to!)} から矢を撃つマスを選択`;

  const cancel = () => {
    setPending(null);
    setFeedback(null);
  };
  const clickSquare = (square: number) => {
    if (!canInteract) return;
    const next = selectAmazonsSquare(legal ?? [], selection, square);
    setPending(next.selection ? { position: positionKey, selection: next.selection } : null);
    setFeedback(next.invalid
      ? { position: positionKey, text: stage === 'select' ? '動かせる自分のアマゾンを選んでください。' : 'このマスには指せません。印のあるマスを選んでください。' }
      : null);
    if (next.move) onMove?.(next.move);
  };

  return (
    <div className={`amazons-frame${mini ? ' mini' : ''}`} data-testid="amazons-board" data-stage={stage} data-ply={state.ply} data-to-move={state.to_move}>
      {!mini && !state.outcome && !staticView && (!suppressTurnStatus || (canInteract && stage !== 'select')) && (
        <div className="amazons-controls">
          <p className="amazons-instruction" role="status" data-testid="amazons-instruction">
            {canInteract ? suppressTurnStatus ? stage === 'move' ? '移動先' : '矢の位置' : instruction : `${sideLabel(state.to_move)}の手番`}
          </p>
        </div>
      )}

      <div className="amazons-board-shell">
        <div className="amazons-grid" data-own-rim={!mini && (yourSide === 'First' || yourSide === 'Second') ? yourSide === 'First' ? 'white' : 'black' : undefined} aria-label="アマゾンズ 10×10盤" onKeyDown={(event) => {
          if (event.key === 'Escape') cancel();
        }}>
          {!mini && <BoardCoords files={cols.map((col) => String.fromCharCode(97 + col))} ranks={rows.map((row) => `${row + 1}`)} />}
          {rows.flatMap((row) => cols.map((col) => {
            const square = row * AMAZONS_SIZE + col;
            const cell = board[square];
            const selectable = canInteract && sources.has(square);
            const isTarget = canInteract && selection?.to === null && targets.has(square);
            const isArrow = canInteract && selection?.to != null && arrows.has(square);
            const selected = selection?.from === square;
            const moved = selection?.to === square;
            const isLast = !mini && !activeProof && last && (last.from === square || last.to === square || last.arrow === square);
            const proofNumber = proofNumbers.get(square);
            const name = cell === 1 ? '白のアマゾン' : cell === 2 ? '黒のアマゾン' : cell === 3 ? '矢' : '空き';
            return (
              <button type="button" key={square} data-testid={`amazons-cell-${square}`} data-square={square} data-cell={cell} data-territory={owners[square]} data-territory-reason={cell === 1 || cell === 2 ? 'occupied' : owners[square] === 'unreachable' ? 'no-amazon' : 'component'}
                className={`amazons-cell ${(row + col) % 2 ? 'light' : 'dark'}${selectable ? ' selectable' : ''}${isTarget ? ' legal-target' : ''}${isArrow ? ' legal-arrow' : ''}${selected ? ' selected' : ''}${moved ? ' pending-to' : ''}${isLast ? ' last-move' : ''}${proofCells.has(square) ? ' proof-region' : ''}`}
                disabled={!canInteract} onClick={() => clickSquare(square)}
                aria-label={`${squareName(square)} ${name}${owners[square] === 'First' ? ' 白の確定地' : owners[square] === 'Second' ? ' 黒の確定地' : owners[square] === 'contested' ? ' 未確定' : owners[square] === 'unreachable' ? cell === 1 || cell === 2 ? ' 占有中' : '' : ''}${selected ? ' 選択中' : isArrow ? ' 矢の候補' : isTarget ? ' 移動先候補' : selectable ? ' 選択可能' : ''}`}
                aria-pressed={selected || moved}
              >
                {(cell === 1 || cell === 2) && <span className={`amazons-piece ${cell === 1 ? 'white' : 'black'}`} aria-hidden>♛</span>}
                {cell === 3 && <span className="amazons-burnt" aria-hidden>×</span>}
                {(isTarget || isArrow) && <span className={`amazons-dot${isArrow ? ' arrow' : ''}`} aria-hidden />}
                {proofNumber != null && <span className="amazons-proof-number" aria-hidden>{proofNumber}</span>}
              </button>
            );
          }))}
        </div>
      </div>

      {!mini && <div className="amazons-remaining" data-testid="amazons-remaining" aria-label="双方の最大残り手数と空きマス数">
        {(['First', 'Second'] as const).map(side => <span key={side} data-side={side} data-exact={remaining[side].exact} title={remaining[side].exact ? '最大手数は確定' : '証明済みの上限。最大手数の厳密値は未確定'}>
          {sideLabel(side)} <strong>{remaining[side].exact ? '' : '≤'}{remaining[side].maximum}</strong>手 / {remaining[side].cells}マス
        </span>)}
      </div>}
      {!mini && feedback?.position === positionKey && canInteract && <p className="amazons-feedback" role="status" data-testid="amazons-feedback">{feedback.text}</p>}
      {showWait && <div className="board-turn-hint" aria-hidden>手番待ち</div>}
      {!mini && state.outcome && (
        <section className="amazons-outcome" aria-label="アマゾンズの勝敗根拠" data-testid="amazons-outcome">
          <h3>{sideLabel(state.outcome.winner)}の勝ち</h3>
          {state.outcome.kind === 'NoMoves'
            ? <p>次の手番の{sideLabel(state.to_move)}に合法手がありません。</p>
            : null}
          {territory && <details className="amazons-proof-details" data-testid="amazons-proof-details">
            <summary>根拠と手順</summary>
            <p data-testid="amazons-territory-reason">{territoryReason(state.outcome)}</p>

            <div className="amazons-regions">
              {territory.regions.map((region, index) => <details key={`${region.owner}-${region.amazons.join('-')}`} className="amazons-region" data-testid={`amazons-region-${index}`}>
                <summary>区画 {index + 1}・{sideLabel(region.owner)}：{regionBoundsLabel(region)}</summary>
                <p>アマゾン {region.amazons.map(squareName).join('、')} / 空き {region.cells.length} マス</p>
                <button type="button" onClick={() => setProof({ position: positionKey, region: index, step: 0 })} data-testid={`amazons-proof-${index}`}>番号付き手順を盤に表示</button>
                {region.line.length > 0 && <ol className="amazons-proof-list">
                  {region.line.map((move, moveIndex) => <li key={`${move.from}-${move.to}-${move.arrow}`}>
                    <button type="button" data-testid={`amazons-proof-step-${index}-${moveIndex + 1}`} aria-pressed={activeProof?.region === index && activeProof.step === moveIndex + 1}
                      onClick={() => setProof({ position: positionKey, region: index, step: moveIndex + 1 })}>{moveNotation(move)}</button>
                  </li>)}
                </ol>}
              </details>)}
            </div>
            {activeProof && activeRegion && <div className="amazons-proof-controls" data-testid="amazons-proof-controls">
              <p role="status">区画 {activeProof.region + 1}：{activeProof.step} / {activeRegion.line.length} 手を再現中</p>
              <button type="button" disabled={activeProof.step === 0} onClick={() => setProof({ ...activeProof, step: activeProof.step - 1 })}>前の手</button>
              <button type="button" disabled={activeProof.step === activeRegion.line.length} onClick={() => setProof({ ...activeProof, step: activeProof.step + 1 })}>次の手</button>
              <button type="button" onClick={() => setProof(null)} data-testid="amazons-proof-close">終局盤面に戻る</button>
            </div>}
          </details>}
        </section>
      )}
    </div>
  );
}
